import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "./LessonList.css";
import axios from "axios";
import Auth from '../../utils/auth';
import { API_URL } from "../../utils/auth";

const LessonList = () => {
  const { lessonId } = useParams(); 
  const [modules, setModules] = useState([]); 
  const [selectedModule, setSelectedModule] = useState(null); 
  const user = Auth.getUser();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchLessonData = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/lesson/lesson?lessonId=${lessonId}&userId=${user.id}`
        );
        const data = response.data;

        const formattedModules = data.modules.map((module, index) => {
          if (index === 0) {
            return {
              id: module.id,
              position: module.position,
              title: module.title,
              vidalyticsId: module.iframe_id,
              description: module.content,
              enabled: true,
              completed: module.completed,
            };
          }
          
          const previousModule = data.modules[index - 1];
          const shouldBeEnabled = previousModule ? previousModule.completed : false;
          
          return {
            id: module.id,
            position: module.position,
            title: module.title,
            vidalyticsId: module.iframe_id,
            description: module.content,
            enabled: shouldBeEnabled,
            completed: module.completed,
          };
        });

        setModules(formattedModules);

        if (formattedModules.length > 0) {
          setSelectedModule(formattedModules[0]);
        }
      } catch (error) {
        console.error("Error fetching lesson data:", error);
      }
    };

    fetchLessonData();
  }, [lessonId, user.id]);

  useEffect(() => {
    if (!selectedModule) return;

    const scriptId = `${selectedModule.vidalyticsId}_script`;
    const existingScript = document.getElementById(scriptId);

    if (existingScript) {
      existingScript.remove();
    }

    const videoContainer = document.getElementById("vidalytics-video-container");
    if (!videoContainer) {
      console.error("The container #vidalytics-video-container does not exist.");
      return;
    }
    videoContainer.innerHTML = `<div id="${selectedModule.vidalyticsId}" style="width:80%; position:relative; padding-top: 56.25%;"></div>`;

    const script = document.createElement("script");
    script.id = scriptId;
    script.type = "text/javascript";
    script.innerHTML = `
      (function (v, i, d, a, l, y, t, c, s) {
          y='_'+d.toLowerCase();c=d+'L';if(!v[d]){v[d]={};}if(!v[c]){v[c]={};}if(!v[y]){v[y]={};}
          var vl='Loader',vli=v[y][vl],vsl=v[c][vl + 'Script'],vlf=v[c][vl + 'Loaded'],ve='Embed';
          if (!vsl){vsl=function(u,cb){
              if(t){cb();return;}
              s=i.createElement("script");s.type="text/javascript";s.async=1;s.src=u;
              if(s.readyState){
                  s.onreadystatechange=function(){if(s.readyState==="loaded"||s.readyState=="complete"){s.onreadystatechange=null;vlf=1;cb();}};
              }else{
                  s.onload=function(){vlf=1;cb();};
              }
              i.getElementsByTagName("head")[0].appendChild(s);
          };}
          vsl(l+'loader.min.js',function(){
              if(!vli){var vlc=v[c][vl];vli=new vlc();}
              vli.loadScript(l+'player.min.js',function(){
                  var vec=v[d][ve];t=new vec();t.run(a);
              });
          });
      })(window, document, 'Vidalytics', '${selectedModule.vidalyticsId}', 'https://fast.vidalytics.com/embeds/56hJ9E4N/${selectedModule.vidalyticsId}/');
    `;

    document.body.appendChild(script);
  }, [selectedModule]);

  const handleCompleteClick = async () => {
    if (!selectedModule || !user) return;

    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/lesson/complete-module`,
        {
          userId: user.id, 
          moduleId: selectedModule.id,
        }
      );

      if (response.status === 200 && response.data.success) {
        const updatedModules = modules.map((module, index) => {
          if (module.id === selectedModule.id) {
            return { ...module, completed: true };
          }
          const currentModuleIndex = modules.findIndex(m => m.id === selectedModule.id);
          if (index === currentModuleIndex + 1) {
            return { ...module, enabled: true };
          }
          return module;
        });

        setModules(updatedModules);

        const currentModuleIndex = modules.findIndex((module) => module.id === selectedModule.id);
        const nextModuleIndex = currentModuleIndex + 1;
        
        if (nextModuleIndex < updatedModules.length) {
          const nextModule = updatedModules[nextModuleIndex];
          if (nextModule.enabled) {
            setSelectedModule(nextModule);
          }
        }
      } else {
        console.error("Failed to mark module as completed.");
      }
    } catch (error) {
      console.error("Error marking module as completed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="lesson-page">
      <div className="video-player-container">
        {selectedModule ? (
          <>
            <h1>{selectedModule.title}</h1>
            <div id="vidalytics-video-container" />
            <div className="complete-button-container">
              {isLoading ? (
                <button className="complete-button" disabled>
                  Loading...
                </button>
              ) : selectedModule.completed ? (
                <></>
              ) : (
                modules.findIndex(m => m.id === selectedModule.id) < modules.length - 1 ? (
                  <button
                    className="complete-button"
                    onClick={handleCompleteClick}
                    disabled={isLoading}
                  >
                    Complete
                  </button>
                ) : (
                  <></>
                )
              )}
            </div>
            <div
              className="lesson-description"
              dangerouslySetInnerHTML={{ __html: selectedModule.description }}
            />
          </>
        ) : (
          <p>Loading...</p>
        )}
      </div>

      <div className="lesson-list-container">
        <ul className="lesson-list">
          {modules.map((module) => (
            <li
              key={module.id}
              onClick={() => module.enabled && setSelectedModule(module)}
              className={selectedModule?.id === module.id ? "active" : ""}
              style={{ 
                opacity: module.enabled ? 1 : 0.5, 
                cursor: module.enabled ? 'pointer' : 'not-allowed',
              }}
            >
              <div className="lesson-title">{module.title}</div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default LessonList;